/* eslint-disable react/jsx-no-duplicate-props */

import React, { Fragment, forwardRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import { withStyles } from '@material-ui/core/styles'

import { ErrorText } from './Text'
import Loading from './Loading'


const styles = theme => ({
  root: {
    minWidth: 200,
  },
  margin: {
    marginTop: theme.spacing(1),
  },
  focused: {},
  formControl: {},
  notchedOutline: {},
  fullWidth: {
    width: '100%',
  },
  secondary: {
    '&$focused $notchedOutline': {
      borderColor: theme.palette.secondary.main,
    },
  },
  primary: {
    '&$focused $notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&$focused $formControl': {
      color: theme.palette.primary.main,
    },
  },
  input: {
    backgroundColor: 'white',
    color: theme.palette.common.black,
    fontSize: 14,
    fontWeight: 300,
    height: 20,
    padding: '10px 20px',

    '&::placeholder, &::-webkit-input-placeholder': {
      color: theme.palette.common.black,
    },
  },
  textarea: {
    color: theme.palette.common.black,
    fontSize: 14,
    fontWeight: 300,
    minHeight: 20,

    '&::placeholder, &::-webkit-input-placeholder': {
      color: theme.palette.common.black,
    },
  },
  error: {
    marginBottom: 0,
  },
  inputDate: {
    width: 175,
    height: 40,
    cursor: 'pointer',
    backgroundColor: 'white',
  },
})


const createKeyPressHandler = fn => e => fn && e.key === 'Enter' && fn(e)


const InputText = forwardRef(({
  tag,
  type,
  color,
  value,
  margin,
  classes,
  onChange,
  multiline,
  className,
  isLoading,
  fullWidth,
  errorText,
  inputProps,
  onPressEnter,
  RightComponent,
  containerClassName,
  ...props
}, ref) => {
  const Container = tag || (errorText ? Fragment : Fragment)
  const containerProps = Container !== Fragment ? {
    className: classNames(containerClassName, {
      [classes.margin]: margin,
      [classes.fullWidth]: fullWidth,
    }),
  } : {}

  const handleChange = (e) => {
    const nextValue = type === 'number' ? +e.target.value : e.target.value

    return onChange && onChange({
      target: {
        value: nextValue,
        name: e.target.name,
      },
    })
  }

  let currentRightComponent = RightComponent

  if (isLoading) {
    currentRightComponent = <Loading />
  }

  return (
    <Container {...containerProps}>
      <TextField
        type={type}
        inputRef={ref}
        variant="outlined"
        value={`${value}`}
        fullWidth={fullWidth}
        onChange={handleChange}
        onKeyDown={createKeyPressHandler(onPressEnter)}
        className={classNames(classes.root, {
          [classes.margin]: margin,
        }, className)}
        inputProps={{
          autoComplete: 'nope',
          className: multiline ? classes.textarea : classes.input,
          ...inputProps,
        }}
        InputLabelProps={{
          shrink: true,
          className: classes.inputLabel,
        }}
        error={!!errorText}
        multiline={multiline}
        InputProps={{
          classes: {
            root: classes[color],
            focused: classes.focused,
            notchedOutline: classes.notchedOutline,
          },
          endAdornment: currentRightComponent && (
            <InputAdornment position="end">
              {currentRightComponent}
            </InputAdornment>
          ),
        }}
        {...props}
      />
      {!!errorText && <ErrorText className={classes.error}>{errorText}</ErrorText>}
    </Container>
  )
})


InputText.defaultProps = {
  type: 'text',
  inputProps: {},
  color: 'primary',
  RightComponent: null,
}


InputText.propTypes = {
  tag: PropTypes.string,
  margin: PropTypes.bool,
  type: PropTypes.string,
  color: PropTypes.string,
  onChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  multiline: PropTypes.bool,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  inputProps: PropTypes.object,
  onPressEnter: PropTypes.func,
  RightComponent: PropTypes.object,
  containerClassName: PropTypes.string,
  classes: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  errorText: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
}


export default withStyles(styles)(InputText)
